import { useState } from 'react';
import { Trans } from 'react-i18next';
import useMeasure from 'react-use-measure';
import {
  CheckCircle,
  Error,
  ExpandLessRounded,
  KeyboardArrowRightRounded,
} from '@mui/icons-material';
import { Button, lighten, Typography } from '@mui/material';

import Color from 'src/assets/_util.scss';
import LoadingSpinner from 'src/components/core/LoadingSpinner/LoadingSpinner';
import { HStack, VStack } from 'src/components/core/Stack';
import { useHighlightsPageContext } from 'src/components/Insights/Catalog/HighlightsPageProvider';
import { AIRequest } from 'src/types/insights';

export function TaggingBannerDetails() {
  const {
    taggingProgress,
    pendingTagRequests,
    succeededTagRequests,
    emptyTagRequests,
    failedTagRequests,
    canceledTagRequests,
    stopTaggingWithAI,
    hideBanner,
    t,
  } = useHighlightsPageContext();
  const { succeededCount, pendingCount, unsuccessfulCount, percent } =
    taggingProgress;
  const [bannerRef, { top }] = useMeasure();

  return (
    <VStack
      sx={{
        maxHeight: `calc(100vh - ${top}px - 40px)`,
        gap: '8px',
        width: '100%',
      }}
      ref={bannerRef}
    >
      <VStack
        sx={{
          gap: '8px',
          overflow: 'hidden auto',
          scrollbarWidth: 'thin',
          padding: '8px 0',
          margin: '-8px 0',
          scrollbarColor: `${Color.foraPurpleLightest} ${Color.foraPurple}`,
        }}
      >
        {!!succeededCount && (
          <TaggingBannerDeeperDetails
            requests={succeededTagRequests}
            backgroundColor={Color.insightsSuccess}
            color={Color.insightsSuccessDark}
          >
            <CheckCircle
              fontSize="medium"
              sx={{ color: Color.insightsSuccess, paddingLeft: '2px' }}
            />
            <Trans
              i18nKey="insights.taggingProgressDetailsSucceeded"
              values={{ succeededCount }}
            />
          </TaggingBannerDeeperDetails>
        )}

        {!!unsuccessfulCount && (
          <TaggingBannerDeeperDetails
            requests={[
              ...failedTagRequests,
              ...emptyTagRequests,
              ...canceledTagRequests,
            ]}
            backgroundColor={Color.insightsFailure}
            color={Color.insightsFailureDark}
          >
            <Error fontSize="medium" sx={{ color: Color.insightsFailure }} />
            <Trans
              i18nKey="insights.taggingProgressDetailsUnsuccessful"
              values={{ unsuccessfulCount }}
            />
          </TaggingBannerDeeperDetails>
        )}

        {!!pendingCount && (
          <TaggingBannerDeeperDetails
            requests={pendingTagRequests}
            backgroundColor={Color.insightsPending}
            color={Color.insightsPendingDark}
          >
            <LoadingSpinner
              active
              size="md"
              thick
              backColor={Color.insightsPending}
              frontColor={Color.insightsPendingDark}
            />
            <Trans
              i18nKey="insights.taggingProgressDetailsPending"
              values={{ pendingCount }}
            />
          </TaggingBannerDeeperDetails>
        )}
      </VStack>

      <Button
        sx={{
          background: Color.danger,
          color: Color.white,
          border: 'none',
          borderRadius: '8px',
          display: percent === 100 && !unsuccessfulCount ? 'none' : 'block',
          fontWeight: 600,
          marginTop: '8px',
          textTransform: 'none',
          ':hover': {
            background: lighten(Color.danger, 0.2),
          },
        }}
        onClick={
          percent === 100 && unsuccessfulCount > 0
            ? () => hideBanner('taggingBanner')
            : stopTaggingWithAI
        }
      >
        {percent === 100 && unsuccessfulCount > 0
          ? t('common.clear')
          : t('common.stop')}
      </Button>
    </VStack>
  );
}

function TaggingBannerDeeperDetails({
  requests,
  backgroundColor,
  color,
  children,
}: {
  requests: AIRequest[];
  backgroundColor?: string;
  color?: string;
  children: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  const ExpandIcon = expanded ? ExpandLessRounded : KeyboardArrowRightRounded;

  return (
    <VStack sx={{ width: '100%' }}>
      <HStack
        sx={{
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <Typography gap="8px" display="flex" variant="body1">
          {children}
        </Typography>
        <ExpandIcon fontSize="small" />
      </HStack>
      {expanded && (
        <ul style={{ marginLeft: '-6px', paddingTop: '4px', marginBottom: 0 }}>
          {requests.map((request) => (
            <TaggingBannerDeeperDetailsText
              key={request.annotation_id}
              request={request}
              backgroundColor={backgroundColor}
              color={color}
            />
          ))}
        </ul>
      )}
    </VStack>
  );
}

function TaggingBannerDeeperDetailsText({
  request,
  backgroundColor,
  color,
}: {
  request: AIRequest;
  backgroundColor?: string;
  color?: string;
}) {
  const { t, scrollToHighlight } = useHighlightsPageContext();
  const requestStatus =
    request.ai_request_status === 'succeeded' && !request.ai_request_code_count
      ? 'empty'
      : request.ai_request_status;

  const tKey = {
    succeeded: 'insights.taggingDeeperDetailsSuccess',
    failed: 'insights.taggingDeeperDetailsFailed',
    empty: 'insights.taggingProgressDetailsEmpty',
    canceled: 'insights.taggingDeeperDetailsCanceled',
    pending: 'insights.taggingDeeperDetailsPending',
    completed: 'insights.taggingDeeperDetailsSuccess',
  }[requestStatus];

  const succeededCount = request.ai_request_code_count ?? 0;

  return (
    <li
      style={{
        display: 'list-item',
        padding: '4px 8px',
        color: backgroundColor,
        cursor: 'pointer',
      }}
      onClick={() => scrollToHighlight(request.annotation_id)}
      onMouseEnter={(e) => {
        e.currentTarget.style.opacity = '0.8';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.opacity = '1';
      }}
    >
      <HStack sx={{ alignItems: 'center' }}>
        <Typography
          sx={{
            backgroundColor,
            color,
            padding: '2px 6px',
            marginRight: '8px',
            borderRadius: '4px',
          }}
        >
          #{request.annotation_id}
        </Typography>
        <Typography sx={{ color: Color.white }}>
          {t(tKey, { succeededCount })}
        </Typography>
      </HStack>
    </li>
  );
}
